import {
  Component,
  computed,
  ElementRef,
  HostListener,
  Inject,
  PLATFORM_ID,
  signal,
  ViewChild,
  ViewEncapsulation,
  Renderer2,
} from '@angular/core';
import { NgbModal, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { AuthComponent } from '../../../../features/auth/auth.component';
import { NgbCollapseModule } from '@ng-bootstrap/ng-bootstrap';
import {
  AccountService,
  AuthService,
  ProductCollectionService,
  QuoteOrdersService,
  SampleOrderService,
} from '@coreApiServices';
import { CommonConstants as Constants, ROUTES, STORAGE } from '@coreConstant';
import { HttpErrorResponse } from '@angular/common/http';
import { CommonConstants } from '@coreConstant';
import { PaginationModel, RequestParamModel, UserModel } from '@coreModels';
import {
  UtilityService,
  SubscribersService,
  TokenStorageService,
  ToastService,
} from '@coreServices';
import { CurrencyPipe, isPlatformBrowser } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { ModalSampleOrderComponent } from 'src/app/features/price-list/modal-sample-order/modal-sample-order.component';
import { HandleImageErrorDirective } from '../../../directives/handle-image-error.directive';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    NgbCollapseModule,
    RouterModule,
    CurrencyPipe,
    HandleImageErrorDirective,
    NgbTooltipModule,
  ],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  encapsulation: ViewEncapsulation.None, // Use this to apply global styles
})
export class HeaderComponent {
  // State variables for collapsed navbar and sticky header
  isHidden = signal(false);
  isSticky = signal(false);
  isSearchActive = signal(false);
  lastScrollTop = 0;
  // Variables for handling user authentication
  public isUserAuthenticated: boolean = false;

  // Collections data for flooring and walling
  flooringCollectionList!: any;
  wallingCollectionList!: any;
  ROUTES = ROUTES;
  @ViewChild('menuIcon') menuIcon!: ElementRef<HTMLButtonElement>;
  isCreditEarnData: any;

  // Constant references
  s3BucketImageURL = Constants.IMAGE_KIT_ENDPOINT;
  wallingTypeName = Constants.WALLING_TYPE_NAME;
  flooringTypeName = Constants.FLOORING_TYPE_NAME;

  //Count header credit,quotes,order,sample
  gathercoCreditCount: number = 0;
  public userData: UserModel = new UserModel();
  isTradeUser: boolean = false;
  showSearchBar: boolean = true;
  quotesCount: any = signal(0);
  orderCount: any = signal(0);
  sampleOrderCount: any = computed(
    () => (this.subscriberService.sampleOrders() || []).length
  );
  destroy$ = new Subject<void>();
  protected readonly CommonConstants = CommonConstants;
  public isCollapsed = true;
  unListen: (() => void) | undefined;
  unListenTouch: (() => void) | undefined;
  isCreditEarnDisabled: any;

  constructor(
    public router: Router,
    private modalService: NgbModal,
    private authService: AuthService,
    private productCollectionService: ProductCollectionService,
    public utilityService: UtilityService,
    public subscriberService: SubscribersService,
    private tokenStorageService: TokenStorageService,
    private quoteOrdersService: QuoteOrdersService,
    private accountServices: AccountService,
    private sampleOrderService: SampleOrderService,
    @Inject(PLATFORM_ID) protected platformId: any,
    private toastService: ToastService,
    private el: ElementRef,
    private renderer: Renderer2
  ) {
    this.isUserAuthenticated = this.authService.isUserAuthenticated();
    this.subscriberService.isUserLoggedIn
      .pipe(takeUntil(this.destroy$))
      .subscribe((options) => {
        this.isUserAuthenticated = options;
        if (this.isUserAuthenticated) {
          this.userData = this.tokenStorageService.getUser();
          // this.isCreditEarnData = this.authService.isrCeditEarnDisabled();
          this.isTradeUser = this.userData.role === Constants.USER_TYPE_TRADE;
          if (this.userData.id) {
            this.getCounts();
            this.getQuoteCount();
            this.getOrderCount();
            this.getSampleOrderCount();
          }
        } else {
          this.userData = new UserModel();
        }
      });
    // Listen for route change and scroll to top
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd && typeof window != 'undefined') {
        this.showSearchBar = event.url != '/' + ROUTES.SEARCH;
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: 'smooth',
        });
      }
    });
    this.getProductCollectionsByType('flooring');
    this.getProductCollectionsByType('walling');
  }

  ngOnInit(): void {
    if (this.isUserAuthenticated) {
      this.userData = this.tokenStorageService.getUser();
      this.getCounts();
      this.getQuoteCount();
      this.getOrderCount();
      this.getSampleOrderCount();
    }
    this.subscriberService.getCountFormLocalStorage
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        if (data) {
          this.getQuoteCount();
          this.getOrderCount();
        }
      });
    this.subscriberService.headerCreditCountUpdate.subscribe((data) => {
      if (data) {
        this.getCounts();
      }
    });
    this.onSidebarBodyScroll();

    this.subscriberService.getCreditFormLocalStorage.subscribe((data) => {
      this.isCreditEarnDisabled = data;
    });
  }

  getSampleOrderCount() {
    this.subscriberService.sampleOrders.set(
      this.tokenStorageService.getSampleOrder()
    );
    this.sampleOrderService.getSampleOrders(this.userData.id, false, true);
  }

  /**
   * Opens the sign-in modal for user authentication.
   */
  showSignInModal() {
    this.modalService.open(AuthComponent, {
      scrollable: true,
      modalDialogClass: 'auth-modal',
    });
  }

  /**
   * Handles window scroll to manage sticky header and navbar visibility.
   */
  @HostListener('window:scroll', [])
  onWindowScroll() {
    if (isPlatformBrowser(this.platformId)) {
      const currentScroll =
        window.pageYOffset || document.documentElement.scrollTop;
      this.isHidden.set(
        this.lastScrollTop > 90 && currentScroll > this.lastScrollTop
      );
      this.lastScrollTop = currentScroll <= 0 ? 0 : currentScroll; // Prevent negative scroll value
      this.isSticky.set(currentScroll > 0); // Make header sticky on scroll
    }
  }

  // Method to handle mouse hover on the menu items
  onMouseEnterMenu(productType: string, event: Event) {
    this.getProductCollectionsByType(productType);

    // Cast the event target to an HTMLElement to access sibling elements
    const target = event.target as HTMLElement;

    // Find the sibling element with the class 'mega-menu'
    const siblingMegaMenu = target.parentElement?.querySelector('.mega-menu');

    // If the sibling element exists, remove the 'style' attribute
    if (siblingMegaMenu) {
      siblingMegaMenu.removeAttribute('style');
    }
  }

  /**
   * Fetches product collections based on the product type (flooring or walling).
   * Data is cached in local storage to reduce API calls.
   * @param productType The type of product to fetch ('flooring' or 'walling').
   */
  // Method to get collections based on the product type (Flooring or Walling)
  getProductCollectionsByType(productType: string) {
    let collectionKey = '';
    let productTypeId = 0;

    // Determine the collection key and product type ID based on the product type
    if (productType === 'flooring') {
      collectionKey = STORAGE.FLOORING_COLLECTIONS_KEY;
      productTypeId = Constants.FLOORING_TYPE_ID; // Flooring type ID
    } else if (productType === 'walling') {
      collectionKey = STORAGE.WALLING_COLLECTIONS_KEY;
      productTypeId = Constants.WALLING_TYPE_ID; // Walling type ID
    }

    // Check if collections data is available in local storage
    const collectionDataString =
      this.tokenStorageService.getItemStorage(collectionKey);
    if (collectionDataString) {
      const collectionData = JSON.parse(collectionDataString);
      if (collectionData) {
        if (productType === 'flooring') {
          this.flooringCollectionList = collectionData; // Load flooring data
        } else if (productType === 'walling') {
          this.wallingCollectionList = collectionData; // Load walling data
        }
      }
    }

    // If no data in local storage, fetch from API
    const requestParamModel = this.createRequestParamModel(productTypeId);
    this.productCollectionService
      .getCollections(requestParamModel)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (dataList: PaginationModel<any>) => {
          requestParamModel.update<any>(dataList); // Update pagination
          // Save data to local storage based on the product type
          if (productType === 'flooring') {
            this.flooringCollectionList = dataList.data;
            this.tokenStorageService.setItemStorage(
              STORAGE.FLOORING_COLLECTIONS_KEY,
              JSON.stringify(dataList.data)
            );
          } else if (productType === 'walling') {
            this.wallingCollectionList = dataList.data;
            this.tokenStorageService.setItemStorage(
              STORAGE.WALLING_COLLECTIONS_KEY,
              JSON.stringify(dataList.data)
            );
          }
        },
        (error: HttpErrorResponse) => {
          this.toastService.showError(error);
        }
      );
  }

  /**
   * Creates a request parameter model based on the provided product type ID.
   * @param productTypeId The ID of the product type (flooring or walling).
   * @returns A populated RequestParamModel object.
   */
  private createRequestParamModel(productTypeId: number): RequestParamModel {
    const requestParamModel = new RequestParamModel();
    requestParamModel.pageSize = Constants.PAGE_SIZE_ALL; // Set page size to fetch all
    requestParamModel.fields = ['id', 'name']; // Specify fields to retrieve
    requestParamModel.addDefaultFilter(
      'product_type_id',
      productTypeId,
      'number'
    ); // Filter by product type ID
    requestParamModel.sortChange('name', 'asc'); // Sort by name in ascending order
    return requestParamModel;
  }

  /**
   * Handles the search event and redirects to the search page with the query.
   * @param event The event object.
   * @param value The search query entered by the user.
   */
  onSearch(event: any, value: any) {
    event.preventDefault();
    this.tokenStorageService.setSearchFields({ search_keyword: value.trim() });
    this.router.navigate([ROUTES.SEARCH]);
  }

  getCounts() {
    if (this.userData.company_id) {
      const requestParamModel = new RequestParamModel();
      requestParamModel.expands = ['gathercoCredit'];
      requestParamModel.fields = [
        'gathercoCredit.available_credit',
        'is_credit_earn_disabled',
      ];
      delete requestParamModel.pageSize;

      this.accountServices
        .getAccountDetails(this.userData.company_id, requestParamModel)
        .subscribe(
          (data: any) => {
            if (data) {
              this.tokenStorageService.setCreditEarn(
                data.is_credit_earn_disabled
              );
              this.subscriberService.getCreditEarnFormLocalStorage(
                data.is_credit_earn_disabled
              );
              if (
                data.gathercoCredit &&
                data.gathercoCredit.available_credit > 0
              ) {
                this.gathercoCreditCount = data.gathercoCredit.available_credit;
              } else {
                this.gathercoCreditCount = 0;
              }
            }
          },
          (error: HttpErrorResponse) => {
            this.gathercoCreditCount = 0;
          }
        );
    }
  }

  /**
   * Logs out the user by clearing authentication tokens.
   */
  logout(): void {
    this.tokenStorageService.clearStorage();
    this.isUserAuthenticated = false; // Update authentication state
    this.subscriberService.isUserLoggedIn.next(false);
    this.router.navigate(['/']);
  }

  handlePriceListClick() {
    this.toggleMenu();
    if (!this.isUserAuthenticated) {
      this.toastService.showError(
        Constants.PREVENT_ACCESS_PRICE_LIST_ERROR_MESSAGE
      );
    }
  }

  toggleMenu(menu?: any) {
    this.isCollapsed = !this.isCollapsed;
    if (window.innerWidth < 1240) {
      this.menuIcon.nativeElement.click();
    }

    if (menu == 'pricelist' && !this.isUserAuthenticated) {
      this.toastService.showError(
        Constants.PREVENT_ACCESS_PRICE_LIST_ERROR_MESSAGE
      );
    }
  }

  toggleMegaMenu(event: Event) {
    const target = event.target as HTMLElement;
    const megaMenu = target.closest('.mega-menu');
    if (megaMenu) {
      (megaMenu as HTMLElement).style.visibility = 'hidden';
    }
  }

  getQuoteCount() {
    const requestParamModel = new RequestParamModel();
    requestParamModel.addDefaultFilter(
      'frontend_user_id',
      this.userData.id,
      'number'
    );
    requestParamModel.addDefaultFilter(
      'type',
      Constants.QUOTE_TYPE_QUOTE,
      'number'
    );
    requestParamModel.addDefaultFilter(
      'is_expired',
      Constants.QUOTE_IS_EXPIRED_NO,
      'number'
    );
    requestParamModel.addDefaultFilter(
      'is_draft',
      Constants.QUOTE_IS_NOT_DRAFT,
      'number'
    );
    requestParamModel.addDefaultFilter('is_deleted', '0', 'number');
    requestParamModel.fields = ['id'];
    this.quoteOrdersService
      .getQuoteOrders(requestParamModel)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (data: any) => {
          this.quotesCount.set(data.totalCount);
        },
        (error: HttpErrorResponse) => {
          this.toastService.showError(error);
        }
      );
  }

  getOrderCount() {
    const requestParamModel = new RequestParamModel();
    requestParamModel.addDefaultFilter(
      'frontend_user_id',
      this.userData.id,
      'number'
    );
    requestParamModel.addDefaultFilter(
      'quote_status',
      Constants.ORDER_TAB_ALL_SHORT,
      'number'
    );
    requestParamModel.addDefaultFilter(
      'is_accepted',
      Constants.QUOTE_IS_ACCEPTED_YES,
      'number'
    );
    requestParamModel.addDefaultFilter(
      'is_expired',
      Constants.QUOTE_IS_EXPIRED_NO,
      'number'
    );
    requestParamModel.addDefaultFilter('is_deleted', '0', 'number');
    requestParamModel.fields = ['id'];
    this.quoteOrdersService
      .getQuoteOrders(requestParamModel)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (data: any) => {
          this.orderCount.set(data.totalCount);
        },
        (error: HttpErrorResponse) => {
          this.toastService.showError(error);
        }
      );
  }

  async openSampleOrderModal() {
    let sample: any = await this.sampleOrderService.getSampleOrders(
      this.userData.id,
      true
    );
    if (!sample?.error && !this.subscriberService.sampleOrderError()) {
      this.modalService.open(ModalSampleOrderComponent, {
        windowClass: 'product-details-modal',
      });
    }
  }

  onSidebarBodyScroll() {
    if (isPlatformBrowser(this.platformId)) {
      const scrollableDiv = this.el.nativeElement.querySelector('.navbar-list');
      this.unListen = this.renderer?.listen(
        scrollableDiv,
        'wheel',
        (event: WheelEvent) => {
          this.preventScroll(event, scrollableDiv);
        }
      );

      this.unListenTouch = this.renderer?.listen(
        scrollableDiv,
        'touchmove',
        (event: TouchEvent) => {
          this.preventScroll(event, scrollableDiv);
        }
      );
    }
  }

  preventScroll(event: Event, scrollableDiv: HTMLElement) {
    const { scrollTop, scrollHeight, clientHeight } = scrollableDiv;
    const atTop =
      scrollTop === 0 &&
      (event instanceof WheelEvent
        ? event.deltaY < 0
        : event instanceof TouchEvent
        ? event.touches[0].clientY > scrollTop
        : false);
    const atBottom =
      scrollTop + clientHeight === scrollHeight &&
      (event instanceof WheelEvent
        ? event.deltaY > 0
        : event instanceof TouchEvent
        ? event.touches[0].clientY < scrollTop + clientHeight
        : false);

    if (atTop || atBottom) {
      event.preventDefault();
    }
  }

  openChangePasswordModal() {
    const modelRef = this.modalService.open(AuthComponent, {
      scrollable: true,
      modalDialogClass: 'auth-modal',
    });
    modelRef.componentInstance.authModel =
      CommonConstants.AUTH_MODAL_TYPES.CHANGE_PASSWORD;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.unListen) {
      this.unListen();
    }

    if (this.unListenTouch) {
      this.unListenTouch();
    }
  }

  protected readonly isPlatformBrowser = isPlatformBrowser;
}
